import {Component, DestroyRef, Input, OnInit} from '@angular/core'
import {TypedFormControls, TypedFormGroup} from '../../../../../../core/forms/form-controls.model'
import {StaticComponentInstance, StaticFormLayoutConfig} from '../../static-form/static-form.types'
import {
  CatDoorInstallation_AssemblyHeight,
  CatDoorInstallation_CatDoor,
  CatDoorInstallation_Frame,
  CatDoorInstallation_GlassStrength,
  CatDoorInstallation_Surface,
  CatDoorInstallation_Target,
  CatDoorInstallationStaticJobQuestionsInput,
  StaticJobQuestionsInput,
} from '../../../../../../../__generated__/types'
import {UntypedFormControl, Validators} from '@angular/forms'
import {conditionalValidator} from '../../../../../../craft/forms/form.types'
import {FormService} from '../../../../../../core/forms/form.service'
import {takeUntilDestroyed} from '@angular/core/rxjs-interop'
import {Observable, of} from 'rxjs'

const minimumCatDoorWindowSizeInCm = 30

@Component({
  selector: 'bgo-cat-door-installation-static-form',
  templateUrl: './cat-door-installation-static-form.component.html',
  styleUrls: ['../../gradual-reveal.css'],
})
export class CatDoorInstallationStaticFormComponent implements StaticComponentInstance, OnInit {
  @Input() form!: TypedFormGroup<CatDoorInstallationStaticJobQuestionsInput>

  readonly InstallationTarget = CatDoorInstallation_Target
  readonly InstallationSurface = CatDoorInstallation_Surface
  readonly GlassStrength = CatDoorInstallation_GlassStrength
  readonly Frame = CatDoorInstallation_Frame
  readonly AssemblyHeight = CatDoorInstallation_AssemblyHeight

  protected minimumCatDoorWindowSizeInCm = minimumCatDoorWindowSizeInCm

  constructor(private readonly destroyRef: DestroyRef) {}

  public static createForm(): TypedFormGroup<CatDoorInstallationStaticJobQuestionsInput> {
    const surface = new UntypedFormControl(undefined, [Validators.required])

    const controls: TypedFormControls<Omit<CatDoorInstallationStaticJobQuestionsInput, '__typename'>> = {
      target: new UntypedFormControl(undefined, [Validators.required]),
      surface,
      glassStrength: new UntypedFormControl(
        undefined,
        conditionalValidator(
          () => !surface.value || surface.value === CatDoorInstallation_Surface.Glass,
          [Validators.required],
        ),
      ),
      frame: new UntypedFormControl(undefined, [Validators.required]),
      height: new UntypedFormControl(undefined, [
        Validators.required,
        Validators.min(minimumCatDoorWindowSizeInCm),
        FormService.numberValidator(),
      ]),
      width: new UntypedFormControl(undefined, [
        Validators.required,
        Validators.min(minimumCatDoorWindowSizeInCm),
        FormService.numberValidator(),
      ]),
      catDoor: new UntypedFormControl(undefined, [Validators.required]),
      assemblyHeight: new UntypedFormControl(undefined, [Validators.required]),
    }

    return new TypedFormGroup<CatDoorInstallationStaticJobQuestionsInput>(controls)
  }

  layout$(): Observable<StaticFormLayoutConfig> {
    return of({
      showBreakdown: true,
    })
  }

  questionsInput(): StaticJobQuestionsInput {
    const value = this.form?.value
    return {
      catDoorInstallation: {
        ...value,
        catDoor: value?.catDoor ?? CatDoorInstallation_CatDoor.Standard,
        target: value?.target ?? CatDoorInstallation_Target.Window,
        glassStrength: value?.surface ? value?.glassStrength : CatDoorInstallation_GlassStrength.Double,
        frame: value?.frame ?? CatDoorInstallation_Frame.WoodMetal,
        surface: value?.surface ?? CatDoorInstallation_Surface.Glass,
        assemblyHeight: value?.assemblyHeight ?? CatDoorInstallation_AssemblyHeight.Above_1M,
        height: value?.height ? Number.parseFloat(value.height.toString()) : minimumCatDoorWindowSizeInCm,
        width: value?.width ? Number.parseFloat(value.width.toString()) : minimumCatDoorWindowSizeInCm,
      },
    }
  }

  ngOnInit(): void {
    this.observeForm()
    this.observeSurfaceControl()
  }

  private observeForm() {
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.form.controls.glassStrength.updateValueAndValidity({emitEvent: false})
    })
  }

  private observeSurfaceControl() {
    this.form.controls.surface.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((v: CatDoorInstallation_Surface) => {
        if (v !== CatDoorInstallation_Surface.Glass) {
          this.form.controls.glassStrength.setValue(undefined)
        }
      })
  }
}
